import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { AuthProvider } from './components/providers/AuthProvider';
import { AuthDialogProvider } from './contexts/AuthDialogContext';
import { ToastProvider } from './contexts/ToastContext';
import { CartProvider } from './contexts/CartContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { MembershipProvider } from './contexts/MembershipContext';
import NetworkProvider from './components/providers/NetworkProvider';
import './index.css';
import App from './App.tsx';
import i18n from './translate.ts';
import { guestSessionManager } from './utils/guestSession';
import { preloadCriticalResources } from './utils/resourcePreloader';
import * as Sentry from '@sentry/react';
import logger from './utils/logger';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/core\.myeztopup\.com/,
    /^https:\/\/stagingcore\.myeztopup\.com/,
    /^https:\/\/devcore\.myeztopup\.com/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, always sample the session when an error occurs.
});

// Inline preloadCriticalFonts function to avoid import issues
const preloadCriticalFonts = () => {
  const criticalFonts = [
    {
      href: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiJ-Ek-_EeA.woff2',
      type: 'font/woff2',
      family: 'Inter',
      weight: '400',
    },
    {
      href: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYAZ9hiA.woff2',
      type: 'font/woff2',
      family: 'Inter',
      weight: '500',
    },
  ];

  criticalFonts.forEach((font) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = font.type;
    link.href = font.href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });

  logger.log('✅ Critical fonts preloaded successfully');
};
import { initializeCSSOptimizations } from './utils/cssOptimization.ts';
import { initializeJSOptimizations } from './utils/jsOptimization.ts';
import { initializeResourceHints } from './utils/resourceHints.ts';

// Global error handlers for unhandled errors
const setupGlobalErrorHandlers = () => {
  // Handle unhandled JavaScript errors
  window.addEventListener('error', (event) => {
    logger.error('🚨 Unhandled Error:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
      stack: event.error?.stack,
    });

    // Prevent the error from causing a white screen
    event.preventDefault();

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      try {
        logger.log('📊 Unhandled error logged for production monitoring');
        // You can integrate with error tracking services here
        // window.errorTracker?.captureException(event.error);
      } catch (loggingError) {
        logger.error('Failed to log unhandled error:', loggingError);
      }
    }
  });

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('🚨 Unhandled Promise Rejection:', {
      reason: event.reason,
      promise: event.promise,
    });

    // Prevent the error from causing a white screen
    event.preventDefault();

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      try {
        logger.log('📊 Unhandled promise rejection logged for production monitoring');
        // You can integrate with error tracking services here
        // window.errorTracker?.captureException(event.reason);
      } catch (loggingError) {
        logger.error('Failed to log unhandled promise rejection:', loggingError);
      }
    }
  });

  logger.log('✅ Global error handlers initialized');
};

// Initialize global error handlers
setupGlobalErrorHandlers();

// Expose guestSessionManager for debugging
(window as unknown as { guestSessionManager: typeof guestSessionManager }).guestSessionManager =
  guestSessionManager;

// Register service worker for caching and performance
if (
  'serviceWorker' in navigator &&
  (import.meta.env.PROD ||
    import.meta.env.MODE === 'staging' ||
    import.meta.env.MODE === 'development' ||
    import.meta.env.MODE === 'production')
) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', {
        scope: '/',
        updateViaCache: 'imports',
      })
      .then((registration) => {
        logger.log('SW registered: ', registration);

        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker is available
                logger.log('New service worker available');
              }
            });
          }
        });
      })
      .catch((registrationError) => {
        logger.log('SW registration failed: ', registrationError);
      });
  });
}

// Performance optimizations for TBT
const optimizePerformance = async () => {
  // Defer heavy library imports
  (window as unknown as { lazyImports: Record<string, () => Promise<unknown>> }).lazyImports = {
    lottie: () => import('lottie-react'),
    colorThief: () => import('colorthief'),
    qrCode: () => import('qrcode.react'),
    html2canvas: () => import('html2canvas'),
  };

  // Preload critical resources for better LCP
  try {
    initializeResourceHints();
    preloadCriticalFonts(); // Preload fonts first for better LCP
    await preloadCriticalResources();
    initializeCSSOptimizations();
    initializeJSOptimizations();
    logger.log('✅ Critical resources preloaded successfully');
  } catch (error) {
    logger.warn('⚠️ Some critical resources failed to preload:', error);
  }

  // Implement resource hints
  const addResourceHints = () => {
    const hints = [
      { rel: 'dns-prefetch', href: '//fonts.googleapis.com' },
      { rel: 'dns-prefetch', href: '//fonts.gstatic.com' },
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: true },
    ];

    hints.forEach((hint) => {
      const link = document.createElement('link');
      Object.assign(link, hint);
      document.head.appendChild(link);
    });
  };

  addResourceHints();
};

// Call optimization function
// Initialize performance optimizations
optimizePerformance().catch((error) => {
  logger.warn('Performance optimization failed:', error);
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <AnalyticsProvider>
          <NetworkProvider>
            <ToastProvider>
              <AuthProvider>
                <AuthDialogProvider>
                  <CartProvider>
                    <MembershipProvider>
                      <App />
                    </MembershipProvider>
                  </CartProvider>
                </AuthDialogProvider>
              </AuthProvider>
            </ToastProvider>
          </NetworkProvider>
        </AnalyticsProvider>
      </BrowserRouter>
    </I18nextProvider>
  </StrictMode>,
);
